'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import HubSpotLegacyForm from '@src/components/Sections/HubSpotLegacyForm';

import Container from '@src/components/Shared/Container';
import NextImage from '@src/components/Shared/NextImage';

import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import SectionHeader from '@src/components/Shared/SectionHeader';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { ContactPageClientPropTypes } from './ContactPage.types';

const ContactPageClient: React.FC<ContactPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/contact`, baseURL: '' })
  );

  setContentType('site-page');

  return (
    <Container className='pt-[4.2rem]'>
      <h1 className='text-header-copy text-[3.2rem] font-medium lg:text-[3.8rem]'>
        {page?.getInTouch?.items?.[0]?.title}
      </h1>
      <div className='lg:flex lg:gap-[3rem]'>
        <div>
          {/* Hubspot form */}
          <HubSpotLegacyForm
            portalId={page?.contactForm?.items?.[0]?.portalId}
            formId={page?.contactForm?.items?.[0]?.formId}
            redirectUrl={page?.contactForm?.items?.[0]?.redirectTo}
            className='valdHsFormBlock'
          />

          <RenderBodyCopy
            className='heading-[1.8rem] pt-[1.6rem] text-[1.2rem] font-normal md:w-[75%]'
            bodyCopy={page?.contactForm?.items?.[0]?.bodyCopy?.json?.content}
          />
        </div>

        <div className='mt-[7rem] flex flex-[60%] flex-col gap-[3rem]'>
          {page?.contactForm?.items?.[0]?.guides?.items?.map((guide) => {
            return (
              <div
                key={guide?.sys?.id}
                className='flex flex-col items-center rounded-lg bg-background-tertiary px-[2.5rem] md:flex-row lg:flex-col lg:pt-[1rem] 3xl:flex-row 3xl:px-[4rem] 4xl:gap-[6rem]'
              >
                <div className='lg:py-0 flex max-w-[30rem] flex-col items-center py-[3rem] text-center md:items-start md:text-left lg:max-w-[30rem] lg:items-center lg:text-center 3xl:items-start 3xl:py-[3rem] 3xl:text-left'>
                  <SectionHeader
                    title={guide?.title}
                    titleClassName='mb-[2rem] text-[2.4rem] font-normal leading-[3rem] text-body-copy--dark'
                    childrenClassName='relative'
                  >
                    <RenderBodyCopy
                      className='text-[1.6rem] text-body-copy'
                      bodyCopy={guide?.bodyCopy?.json?.content}
                    />
                    <RenderButtons
                      className='mt-[2.4rem]! justify-center md:justify-start lg:justify-center 3xl:justify-start'
                      items={guide?.buttonCollection?.items}
                    />
                  </SectionHeader>
                </div>

                <NextImage
                  className='mx-auto h-auto w-[14rem] max-w-[14rem] self-end md:flex-1'
                  src={guide?.mediaPrimary?.url}
                  alt={guide?.mediaPrimary?.description}
                  aspectRatio={guide?.mediaPrimary?.width / guide?.mediaPrimary?.height}
                  width={guide?.mediaPrimary?.width}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Container>
  );
};

export default ContactPageClient;
